import * as Toast from "@kobalte/core/toast"
import { createPolled } from "@solid-primitives/timer"
import { useNavigate } from "@solidjs/router"
import { createMemo, onMount, type ParentProps, Show } from "solid-js"
import { Portal } from "solid-js/web"
import {
  ApplicationContext,
  createSession,
  type User,
} from "@/context/session-provider"
import { syncClass } from "@/context/session-provider.css"
import { Box } from "@/designSystem/components/box"
import { FikaLogo } from "@/designSystem/components/fika-logo"
import { Text } from "@/designSystem/components/text"
import { useI18nContext } from "@/i18n/i18n-solid"
import { useScrollRestoration } from "@/lib/scroll-restoration"
import { toastListClass } from "@/lib/toast.css"
import { Auth } from "@/pages/auth"

export function ApplicationLayout(props: ParentProps) {
  useScrollRestoration()

  const { session, isSuspended } = createSession()
  const { LL } = useI18nContext()

  const loadingPhrases = createMemo(() => [
    LL().applicationLayout.loadingPhrases.synchronizing(),
    LL().applicationLayout.loadingPhrases.hadronCollider(),
    LL().applicationLayout.loadingPhrases.reticulatingSplines(),
    LL().applicationLayout.loadingPhrases.wakingHamsters(),
    LL().applicationLayout.loadingPhrases.consultingOracle(),
    LL().applicationLayout.loadingPhrases.aligningStars(),
    LL().applicationLayout.loadingPhrases.fluxCapacitor(),
    LL().applicationLayout.loadingPhrases.traveling88mph(),
    LL().applicationLayout.loadingPhrases.whiteRabbit(),
    LL().applicationLayout.loadingPhrases.reversingPolarity(),
    LL().applicationLayout.loadingPhrases.enhancingImage(),
    LL().applicationLayout.loadingPhrases.enteringMatrix(),
    LL().applicationLayout.loadingPhrases.findingRing(),
    LL().applicationLayout.loadingPhrases.dividingByZero(),
  ])

  const loadingPhrase = createPolled(
    () =>
      loadingPhrases()[Math.floor(Math.random() * loadingPhrases().length)]!,
    Math.random() * 2000 + 3000,
  )

  return (
    <>
      <Show when={session().status === "loading"}>
        <div class={syncClass}>
          <Box flexDirection="column" gap="8" alignItems="center">
            <FikaLogo withText />
            <Text fontSize="s" color="sand700">
              {loadingPhrase()}
            </Text>
          </Box>
        </div>
      </Show>

      <Show when={session().status === "logged-out"}>
        <Auth isSuspended={isSuspended()} />
      </Show>

      <Show when={session().status === "logged-in" && session().user}>
        {user => (
          <ApplicationContext.Provider value={{ user: user() }}>
            <OnboardingWrapper user={user()}>
              {props.children}
            </OnboardingWrapper>
          </ApplicationContext.Provider>
        )}
      </Show>
      <Portal>
        <Toast.Region swipeDirection="up">
          <Toast.List class={toastListClass} />
        </Toast.Region>
      </Portal>
    </>
  )
}

function OnboardingWrapper(props: ParentProps<{ user: User }>) {
  const navigate = useNavigate()

  onMount(() => {
    // this is because we don't know when a user signs up
    // or logs in, so we need to store this state and then
    // redirect to the appropriate onboarding page
    if (props.user.onboardingStage === "set_username") {
      navigate("/writer-onboarding")
    }
  })

  return props.children
}
